import { z } from "zod";

/**
 * Shared entity schemas. The API validates every inbound payload against these,
 * the admin SPA infers its form types from them, and the scrapers normalize
 * whatever a source hands back into these shapes before writing to DynamoDB.
 */

export const leadSide = z.enum(["demand", "supply"]);
export type LeadSide = z.infer<typeof leadSide>;

export const leadStatus = z.enum([
  "NEW",
  "REVIEWED",
  "CONTACTED",
  "NEGOTIATING",
  "WON",
  "LOST",
  "DISQUALIFIED",
]);
export type LeadStatus = z.infer<typeof leadStatus>;

export const contactInfo = z.object({
  name: z.string().min(1).max(200),
  email: z.email().max(254).optional(),
  phone: z.string().min(7).max(30).optional(),
  website: z.url().max(500).optional(),
  address: z.string().max(500).optional(),
});
export type ContactInfo = z.infer<typeof contactInfo>;

/** What a lead turned into, once it has been converted. */
export const leadConversion = z.object({
  type: z.enum(["CLEANER", "CONTRACT"]),
  id: z.uuid(),
  at: z.iso.datetime(),
});
export type LeadConversion = z.infer<typeof leadConversion>;

/**
 * Which side of the business a contract belongs to.
 *
 * Cleaning and property management are separate businesses that share a
 * customer list: one is recurring work on a site, the other is looking after a
 * property on the owner's behalf. They are priced differently -- per visit or
 * per hour against per door per month or a percentage of rent -- so keeping
 * them apart is what makes "what is each line worth" answerable at all.
 */
export const serviceLine = z.enum(["cleaning", "property-management"]);
export type ServiceLine = z.infer<typeof serviceLine>;

export const lead = contactInfo.extend({
  id: z.uuid(),
  side: leadSide,
  status: leadStatus,
  /** Where this came from: "places", "kijiji", "permits", "website", "manual". */
  source: z.string().min(1).max(50),
  sourceUrl: z.url().max(1000).optional(),
  industry: z.string().max(100).optional(),
  /**
   * Which line of the business this lead is for, carried through to the
   * contract it becomes.
   *
   * Optional rather than defaulted, unlike on a contract: a supply lead is a
   * cleaner and belongs to no service line at all, and most demand leads
   * predate property management. Absent means cleaning.
   */
  serviceLine: serviceLine.optional(),
  /**
   * Approximate road kilometres from Huntsville, worked out at ingest from
   * whatever town the source mentioned.
   *
   * Stored rather than derived on read so the leads table can sort on it
   * without re-parsing every description on every render. Absent means no
   * corridor town was identified -- which is not the same as far away.
   */
  distanceKm: z.number().nonnegative().max(2000).optional(),
  /**
   * Who wrote the ad, and what they wanted, as read off its wording.
   *
   * Hunt's subcontracts to individual cleaners, so an established firm is a
   * competitor rather than a lead, and a company recruiting its own staff is
   * neither. Kijiji mixes all three in one category with no flag to tell them
   * apart, so these are inferred -- and kept as a filter rather than used to
   * drop anything, because the classifier is a reader of words and can be
   * wrong.
   */
  advertiserType: z.enum(["individual", "company", "unknown"]).optional(),
  /** "hiring" means the ad is a vacancy, not someone offering to clean. */
  adIntent: z.enum(["offering", "hiring"]).optional(),
  /** 0-100 fit score, set by the optional Claude enrichment pass. */
  score: z.number().int().min(0).max(100).optional(),
  pitch: z.string().max(500).optional(),
  /**
   * The source's own words, kept whole: a scraped ad body, verbatim.
   *
   * Separate from `pitch` because they answer different questions -- `pitch` is
   * our one-line angle for outreach, this is what they actually wrote. Reading
   * the real ad is how you tell a working cleaner from a franchise recruiter.
   */
  description: z.string().max(5000).optional(),
  /**
   * Set once the lead has become a real record -- a cleaner we work with, or a
   * contract. Kept on the lead rather than only on the new entity so the
   * pipeline can show what came of something without a second lookup, and so
   * converting twice is visibly a mistake.
   */
  convertedTo: leadConversion.optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Lead = z.infer<typeof lead>;

/**
 * Turning a lead into the thing it was always going to become: a supply lead
 * into a cleaner, a demand lead into a contract.
 *
 * `fields` carries whatever the lead cannot know -- a contract's rate, schedule
 * and start date are a negotiation, not something scraped off an ad. It is
 * merged over the values derived from the lead and the result is validated
 * against the target's own schema, so this endpoint cannot create a record the
 * ordinary create route would have rejected.
 */
export const leadConvertRequest = z.object({
  target: z.enum(["cleaner", "contract"]),
  fields: z.record(z.string(), z.unknown()).optional(),
});
export type LeadConvertRequest = z.infer<typeof leadConvertRequest>;

export const inquiryStatus = z.enum(["NEW", "READ", "REPLIED", "ARCHIVED", "SPAM"]);
export type InquiryStatus = z.infer<typeof inquiryStatus>;

/**
 * Public contact-form payload. Unauthenticated, so it stays deliberately small
 * and every field is length-capped. `company` is the honeypot: it is hidden from
 * real users with CSS, so anything non-empty is a bot and gets marked SPAM.
 */
export const inquiryInput = z.object({
  name: z.string().min(1).max(100),
  email: z.email().max(254),
  phone: z.string().max(30).optional(),
  serviceType: z.string().max(100).optional(),
  message: z.string().min(1).max(2000),
  /**
   * Honeypot. Deliberately permissive: `.max(0)` would reject a filled trap at
   * the schema layer and hand the bot a validation error naming the field,
   * which teaches it to omit the field next time. The handler checks this
   * separately and answers with an ordinary success response instead, so a bot
   * cannot distinguish being filtered from getting through.
   */
  company: z.string().max(200).optional(),
});
export type InquiryInput = z.infer<typeof inquiryInput>;

export const inquiry = inquiryInput.omit({ company: true }).extend({
  id: z.uuid(),
  status: inquiryStatus,
  createdAt: z.iso.datetime(),
});
export type Inquiry = z.infer<typeof inquiry>;

/**
 * Public "work with us" application from a cleaner.
 *
 * Kept separate from inquiryInput because it becomes a supply-side lead rather
 * than an inbox enquiry, and it asks different questions. Same honeypot
 * treatment: permissive here, filtered in the handler.
 */
export const applicationInput = z.object({
  name: z.string().min(1).max(100),
  email: z.email().max(254),
  phone: z.string().max(30).optional(),
  /** Free text — towns they cover, split on commas by the handler. */
  coverageArea: z.string().max(300).optional(),
  experience: z.string().max(2000).optional(),
  hasInsurance: z.enum(["yes", "no", "unsure"]).optional(),
  company: z.string().max(200).optional(),
});
export type ApplicationInput = z.infer<typeof applicationInput>;

export const contractStatus = z.enum([
  "DRAFT",
  "ACTIVE",
  "PAUSED",
  "COMPLETED",
  "CANCELLED",
]);
export type ContractStatus = z.infer<typeof contractStatus>;


/**
 * A customer relationship.
 *
 * There is no separate "client": for a cleaning business the customer and the
 * work are the same record, and splitting them meant every screen asked which
 * one you meant. So this holds the contact details, the terms, and the money.
 *
 * The commercial terms are optional because plenty of real records arrive
 * without them -- a customer imported from QuickBooks has a billing history and
 * no rate on file anywhere. Requiring a rate would have meant inventing one.
 *
 * The money fields are a rollup rather than a copy of the ledger. QuickBooks
 * stays the book of record; these exist to answer "who is our biggest account"
 * and "who has gone quiet" without leaving the admin.
 */
export const contract = z.object({
  id: z.uuid(),
  clientName: z.string().min(1).max(200),
  siteAddress: z.string().max(500).optional(),
  scope: z.string().max(2000).optional(),
  status: contractStatus,

  /**
   * Defaulted rather than required: everything that predates property
   * management is cleaning, and so is anything QuickBooks imports without
   * saying otherwise. That makes this safe to read on every existing record.
   */
  serviceLine: serviceLine.default("cleaning"),

  /** Contact details, for the customer rather than the site. */
  email: z.email().max(254).optional(),
  phone: z.string().min(7).max(30).optional(),
  website: z.url().max(500).optional(),
  companyName: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),

  /** Terms. Absent on anything imported rather than negotiated here. */
  rate: z.number().nonnegative().optional(),
  /**
   * "door" and "percent" exist for property management, which is billed per
   * unit per month or as a share of the rent collected rather than per visit.
   * With "percent" the rate is the percentage itself, not dollars.
   */
  rateUnit: z.enum(["hour", "visit", "month", "sqft", "door", "percent"]).optional(),
  /** Units under management. Property management only. */
  unitCount: z.number().int().positive().max(10_000).optional(),
  schedule: z.string().max(200).optional(),
  startDate: z.iso.date().optional(),
  endDate: z.iso.date().optional(),

  /** "quickbooks", "manual", or the lead source it was converted from. */
  source: z.string().max(50).optional(),
  /** The QuickBooks customer id, when this came from there. */
  externalId: z.string().max(64).optional(),
  /** Sum of invoiced amounts, in dollars. Not collected revenue -- see quickbooks.ts. */
  totalBilled: z.number().optional(),
  /** What they currently owe, per QuickBooks. */
  openBalance: z.number().optional(),
  invoiceCount: z.number().int().nonnegative().optional(),
  firstInvoiceDate: z.iso.date().optional(),
  lastInvoiceDate: z.iso.date().optional(),
  /** When the rollup above was last refreshed from QuickBooks. */
  syncedAt: z.iso.datetime().optional(),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Contract = z.infer<typeof contract>;

/**
 * A cleaner Hunt's works with. Legally these are independent subcontractors
 * rather than employees -- which is why insurance and WSIB are tracked per
 * cleaner here rather than assumed -- but everywhere a person reads the word,
 * it is "cleaner".
 */
export const cleaner = contactInfo.extend({
  id: z.uuid(),
  coverageArea: z.array(z.string().max(100)).max(50),
  capabilities: z.array(z.string().max(100)).max(50),
  insuranceExpiry: z.iso.date().optional(),
  wsibExpiry: z.iso.date().optional(),
  notes: z.string().max(2000).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Cleaner = z.infer<typeof cleaner>;

export const adStatus = z.enum(["DRAFT", "POSTED", "EXPIRED"]);
export type AdStatus = z.infer<typeof adStatus>;

/**
 * A recruiting ad for cleaners.
 *
 * Kijiji prohibits automated posting, so this tracks an ad a person places by
 * hand: the copy is drafted here, posted there, and the record is what tells us
 * which regions are currently being advertised and when an ad has lapsed.
 */
/** What an ad is for: hiring cleaners, or selling cleaning. */
export const adPurpose = z.enum(["recruiting", "services"]);

export const ad = z.object({
  id: z.uuid(),
  purpose: adPurpose,
  /** Region slug from the shared Kijiji catalog. */
  region: z.string().min(1).max(50),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(5000),
  status: adStatus,
  postedAt: z.iso.datetime().optional(),
  /** Roughly when the ad lapses, so a stale one stops looking live. */
  expiresAt: z.iso.datetime().optional(),
  /** The live ad, once it has been posted. */
  url: z.url().max(1000).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Ad = z.infer<typeof ad>;
export type AdPurpose = z.infer<typeof adPurpose>;

export const scrapeStatus = z.enum(["RUNNING", "DONE", "FAILED"]);
export type ScrapeStatus = z.infer<typeof scrapeStatus>;

/**
 * What the admin posts to start a scrape.
 *
 * Regions are validated against the shared Kijiji catalog in the handler rather
 * than as an enum here, so adding a region is a one-line change in kijiji.ts.
 */
export const scrapeRequest = z.object({
  regions: z.array(z.string().min(1).max(50)).min(1).max(20),
  maxPagesPerRegion: z.number().int().min(1).max(5),
  /** Which supply sources to search. Validated against the shared catalog. */
  sources: z.array(z.string().min(1).max(50)).min(1).max(10),
});
export type ScrapeRequest = z.infer<typeof scrapeRequest>;

/**
 * A record of one scrape run.
 *
 * The scrape is fired asynchronously and takes far longer than an HTTP request
 * allows, so this is how the admin follows along: the API writes it as RUNNING
 * before invoking the worker, and the worker closes it out as DONE or FAILED.
 */
export const scrapeRun = z.object({
  id: z.uuid(),
  source: z.string().min(1).max(50),
  status: scrapeStatus,
  regions: z.array(z.string().max(50)).max(20),
  sources: z.array(z.string().max(50)).max(10),
  maxPagesPerRegion: z.number().int().min(1).max(5),
  /** Per-source counts, so a run that half-worked reads as such. */
  bySource: z
    .record(
      z.string(),
      z.object({
        found: z.number().int().nonnegative(),
        created: z.number().int().nonnegative(),
        duplicates: z.number().int().nonnegative(),
        errors: z.number().int().nonnegative(),
        failure: z.string().max(500).optional(),
      }),
    )
    .optional(),
  found: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  errors: z.number().int().nonnegative(),
  pagesFetched: z.number().int().nonnegative().optional(),
  /** Present only on FAILED runs. */
  failure: z.string().max(1000).optional(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ScrapeRun = z.infer<typeof scrapeRun>;
